import React, { createContext, useState, useContext, ReactNode } from 'react';
import { AskRequest } from '@hone-automation/common';

interface PanelState {
  isOpen: boolean;
  panelType: string | null;
  data: any;
  title?: string;
  titleIcon?: React.ReactNode;
  showExplicitCloseButton?: boolean;
}

interface QueuePanelContextType {
  panelState: PanelState;
  openPanel: (
    panelType: string,
    options?: {
      data?: any;
      title?: string;
      titleIcon?: React.ReactNode;
      showExplicitCloseButton?: boolean;
    }
  ) => void;
  closePanel: () => void;
}

const QueuePanelContext = createContext<QueuePanelContextType | undefined>(undefined);

export const useQueuePanel = () => {
  const context = useContext(QueuePanelContext);
  if (!context) {
    throw new Error('useQueuePanel must be used within a QueuePanelProvider');
  }
  return context;
};

interface QueuePanelProviderProps {
  children: ReactNode;
}

export const QueuePanelProvider: React.FC<QueuePanelProviderProps> = ({ children }) => {
  const [panelState, setPanelState] = useState<PanelState>({
    isOpen: false,
    panelType: null,
    data: null,
    title: undefined,
    titleIcon: undefined,
    showExplicitCloseButton: false,
  });

  const openPanel = (
    panelType: string,
    options?: {
      data?: any;
      title?: string;
      titleIcon?: React.ReactNode;
      showExplicitCloseButton?: boolean;
    }
  ) => {
    setPanelState({
      isOpen: true,
      panelType,
      data: options?.data || null,
      title: options?.title,
      titleIcon: options?.titleIcon,
      showExplicitCloseButton: options?.showExplicitCloseButton || false,
    });
  };

  const closePanel = () => {
    setPanelState(prev => ({
      ...prev,
      isOpen: false,
    }));
  };

  return (
    <QueuePanelContext.Provider
      value={{
        panelState,
        openPanel,
        closePanel,
      }}
    >
      {children}
    </QueuePanelContext.Provider>
  );
};

export default QueuePanelContext;
